import React from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { getFacetsAsString } from 'Components/widgets/facet-search/FacetSearch.utils';
import { SUGGESTION_TYPES } from 'Components/widgets/global-search/GlobalSearchSuggestionsList';
import {
  facetingCriteriaSelector,
  filteredProductsSelector,
  filteredProductsCountSelector,
} from 'Store/faceting/faceting.selector';
import { keywordsSelector } from 'Store/search/search.selector';
import {
  getDepartureDate,
  getTripDuration,
  getDepartureCity,
} from 'Store/trip-criteria/tripCriteria.selector';
import { getKids, getAdults } from 'Store/trip-information/storeHelper';
import type { Adults } from 'Store/trip-information/adults/adults.reducer';
import 'Store/trip-information/adults/adults.reducer';
import type { Kids } from 'Store/trip-information/kids/kids.reducer';
import 'Store/trip-information/kids/kids.reducer';
import { userInfoSelector } from 'Store/user-info/userInfo.selectors';
import { VIRTUAL_PAGE_LOAD } from 'Tracking/constants';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { COUNTRIES_FACET_ID, DATE_FORMAT, DESTINATIONS_FACET_ID } from 'Utils/faceting/constants';
import { isSummer } from 'Utils/tracking/resort';
import type { ThumbnailProduct } from 'Utils/resort-thumbnail-query/resortThumbnailQuery';
type TrackingSearchResort = {
  resortCode: string;
  resortName: string;
  resortTrident: number;
  resortCountry: string | null | undefined;
  resortRegion: string | null | undefined;
  resortAdult: number | null | undefined;
  resortChildren: number;
  resortDepartureCityCode: string | null | undefined;
  resortSeasonType: string;
  resortUrlImage: string | null | undefined;
  versionBooking: string;
  resortSeason: 'Summer' | 'Winter';
  resortYearSeason: string;
};
type Payload = {
  event: string;
  internalSearchEngineNbResults:
    | 'redirection'
    | 'redirection_editorial'
    | 'redirection_suggestedRequest'
    | number;
  pageName: string;
  resortAdult: number;
  resortChildren: number;
  resortDepartureCityCode: string;
  resortNightNumber: number;
  resortTotalParticipant: number;
  resortTransport: string;
  searchDate: string;
  searchDepartureDate: string;
  searchDestinationArea: string;
  searchDestinationCountry: string;
  searchRequestType: 'Autocomplete' | 'Nocomplete';
  searchTerms: string;
  siteSection: string;
  searchResorts: Array<TrackingSearchResort>;
};
export const useTrackSearchResults = () => {
  const track = useTracking();
  const adults: Array<Adults> = useSelector((state) => getAdults(state) || []);
  const departureCity: string | null | undefined = useSelector((state) => getDepartureCity(state));
  const departureDate: number | null | undefined = useSelector((state) => getDepartureDate(state));
  const duration: number | null | undefined = useSelector((state) => getTripDuration(state));
  const { filters = [] } = useSelector((state) => facetingCriteriaSelector(state));
  const keywords: string | null | undefined = useSelector((state) => keywordsSelector(state));
  const kids: Array<Kids> = useSelector((state) => getKids(state) || []);
  const products: Array<ThumbnailProduct> = useSelector(
    (state) => filteredProductsSelector(state) || [],
  );
  const productsCount: number = useSelector((state) => filteredProductsCountSelector(state) || 0);
  const facetingQuery: SearchQuery = useSelector(({ faceting }) => faceting.query || {});
  const redirection: string | null | undefined = useSelector(
    ({ faceting }) => faceting.redirection,
  );
  const profile: UserInfo = useSelector(userInfoSelector);
  const isFromAutoComplete = Boolean(facetingQuery.type);
  const nbAdults = adults.length;
  const nbKids = kids.length;
  const nbParticipants = nbAdults + nbKids;
  const searchTerms = isFromAutoComplete || facetingQuery.label ? facetingQuery.label : keywords;
  const withFilter = Boolean(
    nbKids > 0 || duration || departureDate || departureCity || filters.length > 0,
  );

  const executeTracking = (payload?: Partial<Payload>, query?: SearchQuery) => {
    const trackingData = {};

    if (!query || query.type !== SUGGESTION_TYPES.EDITO) {
      trackingData.fidelityStatus = profile?.loyaltyProgram?.status || '';
      trackingData.resortAdult = nbAdults;
      trackingData.resortChildren = nbKids;
      trackingData.resortDepartureCityCode = departureCity;
      trackingData.resortNightNumber = duration;
      trackingData.resortTotalParticipant = nbParticipants;
      trackingData.resortTransport = departureCity ? 'with Transport' : null;
      trackingData.searchDepartureDate = departureDate
        ? moment(departureDate).format(DATE_FORMAT)
        : null;
      trackingData.searchDestinationArea = getFacetsAsString(filters, DESTINATIONS_FACET_ID);
      trackingData.searchDestinationCountry = getFacetsAsString(filters, COUNTRIES_FACET_ID);
      trackingData.searchFilter = withFilter ? 'withFilter' : 'withoutFilter';
      trackingData.searchResorts = products.map<TrackingSearchResort>((product) => ({
        resortCode: product.id,
        resortName: product.name,
        resortTrident: product.comfort,
        resortCountry: product.destination ? product.destination.country : null,
        resortRegion: product.destination ? product.destination.area : null,
        resortAdult: nbAdults,
        resortChildren: nbKids,
        resortDepartureCityCode: departureCity,
        resortSeasonType: product.seasonName || 'Summer',
        resortUrlImage: product.image,
        versionBooking: 'responsive',
        resortSeason: departureDate && isSummer(new Date(departureDate)) ? 'Summer' : 'Winter',
        resortYearSeason: moment(departureDate).format('YYYY'),
      }));
    }

    track({
      event: VIRTUAL_PAGE_LOAD,
      internalSearchEngineNbResults: redirection ? 'redirection' : productsCount,
      pageName: 'shopping_home_search',
      searchDate: moment().format(DATE_FORMAT),
      searchRequestType: isFromAutoComplete ? 'Autocomplete' : 'Nocomplete',
      searchTerms: searchTerms || '',
      ...trackingData,
      ...payload,
    });
    // Reset searchReferrer after each pageView is sent.
    // If searchReferrer === "null", GTM will set it to "search"
    track({
      event: 'PUSH',
      searchReferrer: 'null',
    });
  };

  return executeTracking;
};
